import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { MID, TINY, marketingPalette } from "@/components/marketing/tokens";
import { localSrcsetFor } from "@/components/marketing/localSrcset";
import HomeTabs from "./HomeTabs";
import { InlineSvg } from "@/components/marketing/Marquee";
import { DASHBOARD } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

const STROKE = {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/**
 * One glyph per tab, in the published order. Inline rather than vendored: they
 * are drawn in the tab's own colour, which changes with selection.
 */
const TAB_ICONS: ReactNode[] = [
  <>
    <path
      d="M15.0127 17.854C15.1667 17.2174 15.4932 16.6357 15.9563 16.1726C16.4194 15.7095 17.0011 15.383 17.6377 15.229C17.0011 15.075 16.4194 14.7485 15.9563 14.2854C15.4932 13.8223 15.1667 13.2406 15.0127 12.604C14.8587 13.2406 14.5322 13.8223 14.0691 14.2854C13.606 14.7485 13.0243 15.075 12.3877 15.229C13.0243 15.383 13.606 15.7095 14.0691 16.1726C14.5322 16.6357 14.8587 17.2174 15.0127 17.854Z"
      {...STROKE}
    />
    <path
      d="M3.0127 6.229C3.0127 5.63227 3.24975 5.05997 3.67171 4.63801C4.09366 4.21606 4.66596 3.979 5.2627 3.979H14.2627C14.8594 3.979 15.4317 4.21606 15.8537 4.63801C16.2756 5.05997 16.5127 5.63227 16.5127 6.229V7.729C16.5127 8.32574 16.2756 8.89804 15.8537 9.31999C15.4317 9.74195 14.8594 9.979 14.2627 9.979H5.2627C4.66596 9.979 4.09366 9.74195 3.67171 9.31999C3.24975 8.89804 3.0127 8.32574 3.0127 7.729V6.229Z"
      {...STROKE}
    />
    <path
      d="M9.7627 15.979H5.2627C4.66596 15.979 4.09366 15.742 3.67171 15.32C3.24975 14.898 3.0127 14.3257 3.0127 13.729V12.229C3.0127 11.6323 3.24975 11.06 3.67171 10.638C4.09366 10.2161 4.66596 9.979 5.2627 9.979H13.1377"
      {...STROKE}
    />
    <path d="M6.0127 6.979V6.98484" {...STROKE} />
    <path d="M6.0127 12.979V12.9848" {...STROKE} />
  </>,
  <>
    <path
      d="M4.5127 3.979H7.5127C7.71161 3.979 7.90237 4.05802 8.04303 4.19867C8.18368 4.33933 8.2627 4.53009 8.2627 4.729V9.229C8.2627 9.42792 8.18368 9.61868 8.04303 9.75933C7.90237 9.89999 7.71161 9.979 7.5127 9.979H4.5127C4.31378 9.979 4.12302 9.89999 3.98237 9.75933C3.84171 9.61868 3.7627 9.42792 3.7627 9.229V4.729C3.7627 4.53009 3.84171 4.33933 3.98237 4.19867C4.12302 4.05802 4.31378 3.979 4.5127 3.979Z"
      {...STROKE}
    />
    <path
      d="M4.5127 12.979H7.5127C7.71161 12.979 7.90237 13.058 8.04303 13.1987C8.18368 13.3393 8.2627 13.5301 8.2627 13.729V15.229C8.2627 15.4279 8.18368 15.6187 8.04303 15.7593C7.90237 15.9 7.71161 15.979 7.5127 15.979H4.5127C4.31378 15.979 4.12302 15.9 3.98237 15.7593C3.84171 15.6187 3.7627 15.4279 3.7627 15.229V13.729C3.7627 13.5301 3.84171 13.3393 3.98237 13.1987C4.12302 13.058 4.31378 12.979 4.5127 12.979Z"
      {...STROKE}
    />
    <path
      d="M12.0127 9.979H15.0127C15.2116 9.979 15.4024 10.058 15.543 10.1987C15.6837 10.3393 15.7627 10.5301 15.7627 10.729V15.229C15.7627 15.4279 15.6837 15.6187 15.543 15.7593C15.4024 15.9 15.2116 15.979 15.0127 15.979H12.0127C11.8138 15.979 11.623 15.9 11.4824 15.7593C11.3417 15.6187 11.2627 15.4279 11.2627 15.229V10.729C11.2627 10.5301 11.3417 10.3393 11.4824 10.1987C11.623 10.058 11.8138 9.979 12.0127 9.979Z"
      {...STROKE}
    />
    <path
      d="M12.0127 3.979H15.0127C15.2116 3.979 15.4024 4.05802 15.543 4.19867C15.6837 4.33933 15.7627 4.53009 15.7627 4.729V6.229C15.7627 6.42792 15.6837 6.61868 15.543 6.75933C15.4024 6.89999 15.2116 6.979 15.0127 6.979H12.0127C11.8138 6.979 11.623 6.89999 11.4824 6.75933C11.3417 6.61868 11.2627 6.42792 11.2627 6.229V4.729C11.2627 4.53009 11.3417 4.33933 11.4824 4.19867C11.623 4.05802 11.8138 3.979 12.0127 3.979Z"
      {...STROKE}
    />
  </>,
  <>
    <path
      d="M8.26273 3.37899C7.08951 3.65097 6.01042 4.23189 5.13739 5.06149C4.26436 5.8911 3.62919 6.93917 3.29775 8.097C2.96632 9.25483 2.95069 10.4803 3.2525 11.6462C3.5543 12.8121 4.16255 13.876 5.01414 14.7276C5.86573 15.5792 6.92966 16.1874 8.09556 16.4892C9.26147 16.791 10.4869 16.7754 11.6447 16.444C12.8026 16.1125 13.8506 15.4774 14.6802 14.6043C15.5098 13.7313 16.0907 12.6522 16.3627 11.479C16.3627 11.2801 16.2837 11.0893 16.1431 10.9487C16.0024 10.808 15.8116 10.729 15.6127 10.729H10.5127C10.1149 10.729 9.73337 10.571 9.45207 10.2897C9.17076 10.0083 9.01273 9.62682 9.01273 9.22899V3.97899C9.00341 3.89013 8.97654 3.80401 8.93369 3.72562C8.89084 3.64722 8.83284 3.57812 8.76308 3.5223C8.69331 3.46649 8.61316 3.42508 8.52727 3.40048C8.44138 3.37588 8.35146 3.36858 8.26273 3.37899Z"
      {...STROKE}
    />
    <path
      d="M12.0127 3.604C12.965 3.93932 13.8299 4.48398 14.5438 5.19787C15.2577 5.91177 15.8024 6.77671 16.1377 7.729H12.7627C12.5638 7.729 12.373 7.64999 12.2324 7.50933C12.0917 7.36868 12.0127 7.17792 12.0127 6.979V3.604Z"
      {...STROKE}
    />
  </>,
];

export default function WhyDashboard() {
  return (
    <Box>
      <Box component="section">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            width: 1,
            maxWidth: "48rem",
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography component="h2" sx={HEADING_2}>
            {DASHBOARD.heading}
          </Typography>
          {/* Three fifths of the title wrap, the whole of it on a phone. */}
          <Typography
            component="p"
            sx={[BODY_SMALL, { width: "60%", [TINY]: { width: 1 } }]}
          >
            {DASHBOARD.body}
          </Typography>
        </Box>
      </Box>
      {/*
        The gap above the tabs is the section's padding, not the list's margin:
        the rule below is positioned from the section's own top edge, and a
        margin on the first in-flow child would collapse out and take the
        section with it, dropping the rule clear of the tabs.
      */}
      <Box
        component="section"
        sx={{ position: "relative", pt: "2.5rem", [TINY]: { pt: "1.5rem" } }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "auto 0 0",
            zIndex: 7,
            height: "9rem",
            background:
              "linear-gradient(rgba(255, 255, 255, 0) 17%, #FFFFFF 84%)",
            [TINY]: { height: "7rem" },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "5rem 0 auto",
            zIndex: 4,
            height: "1px",
            backgroundColor: marketingPalette.outline,
            [MID]: { display: "none" },
          }}
        />
        <HomeTabs
          idPrefix="home-dashboard"
          listSx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: 1,
            [TINY]: { gap: "6.4px" },
          }}
          tabSx={(selected) => ({
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            p: "8px 24px",
            fontFamily: "inherit",
            fontSize: 15,
            // The published tab states its own leading; left to `normal` the
            // row is 4px shorter and the underline rides up with it.
            lineHeight: 1.5,
            color: marketingPalette.ink,
            backgroundColor: selected ? "#F3F6F8" : "background.default",
            borderRadius: "6px 6px 0 0",
            borderBottom: `2px solid ${selected ? marketingPalette.inkSubtle : "#FFFFFF"}`,
            transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
            "&:hover": { backgroundColor: marketingPalette.tabHoverTint },
            [MID]: { px: "1rem" },
            [TINY]: { px: "0.4rem", fontSize: 14 },
          })}
          labels={DASHBOARD.tabs.map((tab, index) => (
            <Box
              key={tab.label}
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <InlineSvg
                viewBox="0 0 19 19"
                size="1.2rem"
                sx={{ [TINY]: { display: "none" } }}
              >
                {TAB_ICONS[index]}
              </InlineSvg>
              {tab.label}
            </Box>
          ))}
          panelsWrapperSx={{ p: "1rem", [TINY]: { p: "0 1rem" } }}
          panels={DASHBOARD.tabs.map((tab) => (
            <Box
              key={tab.label}
              sx={{
                position: "relative",
                width: 1,
                mx: "auto",
                // The pane is aspect-locked by its own padding and the visual is
                // absolute inside it, so the tab bar cannot jump on a switch.
                pt: "57%",
                [MID]: { pt: "140%" },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  borderRadius: "8px",
                  backgroundColor: "background.default",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={tab.wide}
                  srcSet={localSrcsetFor(tab.wide, DASHBOARD.wideSrcsetWidth)}
                  sizes="100vw"
                  alt=""
                  loading="lazy"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: 1,
                    height: 1,
                    objectFit: "cover",
                    [MID]: { display: "none" },
                  }}
                />
                <Box
                  component="img"
                  src={tab.narrow}
                  srcSet={localSrcsetFor(
                    tab.narrow,
                    DASHBOARD.narrowSrcsetWidth
                  )}
                  sizes="100vw"
                  alt=""
                  loading="lazy"
                  sx={{
                    display: "none",
                    [MID]: {
                      display: "block",
                      position: "absolute",
                      inset: 0,
                      width: 1,
                    },
                  }}
                />
              </Box>
            </Box>
          ))}
        />
      </Box>
    </Box>
  );
}
